import cn from 'clsx'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'

import FilterToggle from './FilterToggle'

import { tripPlannerItemFilterTabClick } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import LineListUlIcon from 'components/Luxkit/Icons/line/LineListUlIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppDispatch } from 'hooks/reduxHooks'
import { EmptyArray, intersectArrays } from 'lib/array/arrayUtils'
import TripContext, { TripContextValue } from 'tripPlanner/contexts/TripContext'
import { FilterIcons, FilterLabels } from 'tripPlanner/models'
import { clearSubFilters, setSubFilters } from 'tripPlanner/reducers/actions'
import { TripPlannerFilterTypeCount } from 'tripPlanner/utils/summary'

const Container = styled.div`
  padding: ${rem(8)} ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(8)} ${rem(40)};
  }

  &.no-horizontal-padding {
    padding: ${rem(8)} ${rem(0)};
  }
`

type Variant = 'drawer' | 'tripSummary'

interface Props {
  itemCount: number
  availableSubFilters: TripPlannerFilterTypeCount[]
  selectedFilters: App.TripPlannerSubFilterType[]
  desktopGutterSize?: number
  variant: Variant
}

function TripItemFilters({
  availableSubFilters = EmptyArray,
  selectedFilters,
  itemCount,
  desktopGutterSize = 40,
  variant,
}: Props) {
  const dispatch = useAppDispatch()
  const tripContext = useContext<TripContextValue | null>(TripContext)

  useEffect(() => {
    dispatch(clearSubFilters())

    // Clearing filter selection upon filter component unmount
    return () => {
      dispatch(clearSubFilters())
    }
  }, [dispatch])

  useEffect(() => {
    const selectedAvailableFilters = intersectArrays(
      selectedFilters,
      availableSubFilters.map((filter) => filter.filterType),
    )
    if (selectedAvailableFilters.length < selectedFilters.length) {
      dispatch(setSubFilters(selectedAvailableFilters))
    }
  }, [availableSubFilters, selectedFilters, dispatch])

  const clearFilters = useCallback(() => {
    if (variant === 'tripSummary') {
      fireInteractionEvent(tripPlannerItemFilterTabClick('all'))
    }
    dispatch(clearSubFilters())
  }, [dispatch, variant])

  const setSubFilter = useCallback(
    (filterType: App.TripPlannerSubFilterType) => {
      if (variant === 'tripSummary') {
        fireInteractionEvent(tripPlannerItemFilterTabClick(filterType))
      }
      // Using setSubFilters as UX are trialling only allowing one filter at a time
      dispatch(setSubFilters([filterType]))
    },
    [dispatch, variant],
  )

  const selectedFiltersSet = useMemo(
    () => new Set(selectedFilters),
    [selectedFilters],
  )

  return (
    <Container
      className={cn({ 'no-horizontal-padding': variant === 'drawer' })}
    >
      <CardCarousel
        gap={8}
        gutterSize={24}
        desktopGutterSize={desktopGutterSize}
        gutterStyle="overflow"
        snap="start"
        pageSize="auto"
        arrowsOnlyOnHover
      >
        <FilterToggle
          key="allItems"
          data-testid="trip-filters-all"
          onClick={clearFilters}
          selected={selectedFiltersSet.size === 0}
          iconComponent={LineListUlIcon}
          label="All"
          count={itemCount}
        />
        {availableSubFilters.map((filter: TripPlannerFilterTypeCount) => {
          const { filterType, count } = filter
          const Icon = FilterIcons[filterType]

          if (tripContext?.viewAsTemplate && filterType === 'Note') {
            return null
          }

          return (
            <FilterToggle
              key={filterType}
              selected={selectedFiltersSet.has(filterType)}
              data-testid={'trip-filters-' + filterType}
              onClick={() => setSubFilter(filterType)}
              iconComponent={Icon}
              label={FilterLabels[filterType]}
              count={count}
            />
          )
        })}
      </CardCarousel>
    </Container>
  )
}

export default TripItemFilters
